/**
 * DueDateDisplay Component
 * Smart date display with overdue indicator and days-until-due
 */

import { Group, Text, Badge } from '@/components/ui';
import { IconClock, IconAlertTriangle } from '@tabler/icons-react';
import { formatDate, getDaysUntilDue, isTaskOverdue } from '../utils/taskHelpers';

interface DueDateDisplayProps {
  dueDate?: string | Date | null;
  statusID?: number;
  isOverdue?: boolean;
  showDaysUntil?: boolean;
  size?: 'xs' | 'sm' | 'md';
}

export function DueDateDisplay({
  dueDate,
  statusID,
  isOverdue: isOverdueProp,
  showDaysUntil = true,
  size = 'sm',
}: DueDateDisplayProps) {
  if (!dueDate) {
    return <Text size={size} c="dimmed">No due date</Text>;
  }

  const overdue = isOverdueProp ?? isTaskOverdue({ dueDate, statusID });
  const daysUntil = getDaysUntilDue(dueDate);

  const getDaysLabel = () => {
    if (daysUntil === null) return null;
    if (daysUntil < 0) return `${Math.abs(daysUntil)}d overdue`;
    if (daysUntil === 0) return 'Due today';
    if (daysUntil === 1) return 'Due tomorrow';
    if (daysUntil <= 7) return `${daysUntil}d left`;
    return null;
  };

  const daysLabel = showDaysUntil ? getDaysLabel() : null;

  return (
    <Group gap={6} wrap="nowrap">
      {overdue ? (
        <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />
      ) : (
        <IconClock size={14} color="var(--mantine-color-dimmed)" />
      )}
      <Text
        size={size}
        c={overdue ? 'red' : undefined}
        fw={overdue ? 600 : undefined}
      >
        {formatDate(dueDate)}
      </Text>
      {daysLabel && (
        <Badge
          size="xs"
          variant="light"
          color={overdue ? 'red' : daysUntil !== null && daysUntil <= 3 ? 'orange' : 'blue'}
        >
          {daysLabel}
        </Badge>
      )}
    </Group>
  );
}
